/**
 * Fetch bridge for the vendored diagnosis panel (W7b / F3).
 *
 * The POC panel fetched /api/diagmap, /api/cause_detail and /api/neo4j itself, unauthenticated.
 * In the main app every call goes to /api/v5/brain/* (brain_api.schema.json) through the fetcher the
 * React host injects (the app's authFetch, which adds the Bearer token and X-Arta-Role), always with the
 * current session_id. The vendored code never imports app modules: the host calls configureBrainApi().
 */

export const BRAIN_API_BASE = "/api/v5/brain";
export const BRAIN_ENDPOINTS = Object.freeze({
  CATEGORY: "category",
  DIAGMAP: "diagmap",
  CAUSE_DETAIL: "cause_detail",
  STORED: "stored",
});
const KNOWN_ENDPOINTS = new Set(Object.values(BRAIN_ENDPOINTS));
const HTTP_NOT_CONFIGURED = 503;

let config = { fetcher: null, sessionId: "" };

/** Set by BrainPanelHost: `fetcher(url, init)` must return a fetch Response. */
export function configureBrainApi(next) {
  config = { ...config, ...(next || {}) };
}

export function resetBrainApi() {
  config = { fetcher: null, sessionId: "" };
}

export const brainSessionId = () => config.sessionId || "";

export function brainUrl(endpoint, params) {
  if (!KNOWN_ENDPOINTS.has(endpoint)) throw new Error(`Unknown brain endpoint: ${endpoint}`);
  const query = new URLSearchParams();
  const all = { session_id: config.sessionId, ...(params || {}) };
  Object.entries(all).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== "") query.set(k, String(v));
  });
  return `${BRAIN_API_BASE}/${endpoint}?${query.toString()}`;
}

export const BRAIN_NETWORK_FAILURE = 0;
const FAILURE_REASONS = Object.freeze({
  [BRAIN_NETWORK_FAILURE]: "the server did not answer. Check that the backend is running and the connection is up.",
  401: "your sign-in has expired. Sign in again, then try again.",
  403: "the server does not link this session to your account (it may have been started in another sign-in).",
  404: "the server has no stored state for this step yet.",
  405: "the server does not accept this request (front end and server versions differ). Reload the page.",
  409: "the diagnosis graph changed since this step. Answer the next question or try again.",
  503: "the diagnosis graph is not reachable right now.",
});

/**
 * One line that says why a brain call failed, by HTTP status (0 = the network call itself failed).
 * Server error text is appended for 4xx answers (it names the missing state); 5xx text is generic by design.
 */
export function describeBrainFailure(status, data) {
  const code = Number.isInteger(status) ? status : BRAIN_NETWORK_FAILURE;
  const reason = FAILURE_REASONS[code] || `the server could not build it (status ${code}).`;
  const detail = data && typeof data.error === "string" && code >= 400 && code < 500 && !FAILURE_REASONS[code] ? ` (${data.error})` : "";
  return `${reason}${detail}`;
}

/**
 * GET one brain endpoint. Resolves {ok, status, data}; never rejects for an HTTP error (the panel
 * shows its own "not available" text), rejects only when the network call itself fails.
 */
export async function brainGet(endpoint, params) {
  if (typeof config.fetcher !== "function" || !config.sessionId) {
    return { ok: false, status: HTTP_NOT_CONFIGURED, data: { kind: "error", error: "The diagnosis panel is not connected to a session.", status: HTTP_NOT_CONFIGURED } };
  }
  const res = await config.fetcher(brainUrl(endpoint, params), { headers: { Accept: "application/json" } });
  let data = null;
  try {
    data = await res.json();
  } catch (_) {
    data = null;
  }
  return { ok: !!res.ok, status: res.status, data };
}
